import { Alert, IconButton, Slide, Snackbar, Tooltip, useTheme } from "@mui/material";
import { SnackbarData } from "../../api/types/commonTypes";
import { useTranslation } from "react-i18next";
import IconWrapper from "./IconWrapper";
import { BsX } from "react-icons/bs";

const CloseIcon = IconWrapper(BsX);

export type CustomSnackbarProps = {
    snackbar: SnackbarData;
    handleCloseSnackbar: () => void;
    autoHideDuration?: number;
    vertical?: "top" | "bottom";
    horizontal?: "left" | "center" | "right";
};

/**
 * CustomSnackbar displays a styled `Snackbar` component using Material UI's `Alert`.
 * It supports different severity levels, positioning, auto-hide, and a close button.
 *
 * Props:
 * - `snackbar` SnackbarData:
 *   The state object representing Snackbar visibility and content.
 * - `handleCloseSnackbar` (function) - Callback to close the Snackbar.
 * - `autoHideDuration` (number, optional) - Time in milliseconds before the Snackbar auto-hides. Default is `3000`.
 * - `vertical` ("top" | "bottom", optional) - Vertical position on screen. Default is `"bottom"`.
 * - `horizontal` ("left" | "center" | "right", optional) - Horizontal position on screen. Default is `"center"`.
 *
 * @param {CustomSnackbarProps} props - The component props.
 * @returns {JSX.Element} A Material UI `Snackbar` component with customized styling and behavior.
 *
 * @example
 * <CustomSnackbar
 *   snackbar={{
 *     open: true,
 *     message: "Item deleted successfully",
 *     severity: "success",
 *   }}
 *   handleCloseSnackbar={() => console.log("Snackbar closed")}
 *   autoHideDuration={3000}
 *   vertical="top"
 *   horizontal="right"
 * />
 */

function CustomSnackbar({ snackbar, handleCloseSnackbar, autoHideDuration = 3000, vertical = "bottom", horizontal = "center" }: CustomSnackbarProps) {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Snackbar
            open={snackbar.open}
            autoHideDuration={autoHideDuration}
            onClose={handleCloseSnackbar}
            TransitionComponent={Slide}
            anchorOrigin={{ vertical: vertical, horizontal: horizontal }}
        >
            <Alert
                onClose={handleCloseSnackbar}
                severity={snackbar.severity}
                sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: "1px solid",
                    borderColor: snackbar.severity === "error" ? theme.palette.red : theme.palette.divider,
                    borderRadius: "6px",
                    backgroundColor: theme.palette.cardBackground,
                    color: theme.palette.text.primary
                }}
                action={
                    <Tooltip title={t("common.close")} placement="top">
                        <IconButton
                            size="small"
                            aria-label="close"
                            sx={{
                                color: theme.palette.secondaryIcon,
                                "&:hover": {
                                    color: theme.palette.secondaryIcon
                                }
                            }}
                            onClick={handleCloseSnackbar}
                        >
                            <CloseIcon style={{ height: "28px", width: "28px" }} />
                        </IconButton>
                    </Tooltip>
                }
            >
                {snackbar.message}
            </Alert>
        </Snackbar>
    );
}

export default CustomSnackbar;
